import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Users, Search, Filter, Edit2, Shield, AlertTriangle, 
  CheckCircle, XCircle, Loader, Eye, History, Crown,
  ChevronDown, RefreshCw, FileText, MapPin, Phone, Building2, Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Planos disponíveis
const PLANS = [
  { id: 'basic', name: 'Basic', color: 'bg-slate-500' },
  { id: 'premium', name: 'Premium', color: 'bg-gray-500' },
  { id: 'pro', name: 'Pro', color: 'bg-blue-500' },
  { id: 'advanced', name: 'Advanced', color: 'bg-purple-500' },
  { id: 'scale', name: 'Scale', color: 'bg-indigo-500' },
  { id: 'business', name: 'Business', color: 'bg-orange-500' },
  { id: 'elite', name: 'Elite', color: 'bg-amber-500' },
  { id: 'exclusive', name: 'Exclusive', color: 'bg-gradient-to-r from-amber-500 to-yellow-500' }
];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  suspended: 'bg-red-100 text-red-800'
};

export const AdminManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  
  // Edit Plan Dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [adminOverride, setAdminOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // History Dialog
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [planHistory, setPlanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // View As Dialog
  const [isViewAsDialogOpen, setIsViewAsDialogOpen] = useState(false);
  const [viewAsUser, setViewAsUser] = useState(null);

  // Profile Dialog
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/all`, { withCredentials: true });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Apply filters
  useEffect(() => {
    let result = users;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u => 
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.store_id?.includes(term)
      );
    }
    
    if (roleFilter !== 'all') {
      result = result.filter(u => u.role === roleFilter);
    }
    
    if (planFilter !== 'all') {
      result = result.filter(u => u.subscription?.plan === planFilter);
    }
    
    setFilteredUsers(result);
  }, [users, searchTerm, roleFilter, planFilter]);

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setNewPlan(user.subscription?.plan || 'premium');
    setAdminOverride(user.subscription?.admin_override || false);
    setIsEditDialogOpen(true);
  };

  const handleUpdatePlan = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/users/${selectedUser.user_id}/plan`,
        {
          plan: newPlan,
          admin_override: adminOverride
        },
        { withCredentials: true }
      );
      toast.success('Plano atualizado com sucesso!');
      setIsEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar plano');
    } finally {
      setSaving(false);
    }
  };

  const fetchPlanHistory = async (userId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin/users/${userId}/plan-history`,
        { withCredentials: true }
      );
      setPlanHistory(response.data.history || []);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
      setPlanHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openHistoryDialog = (user) => {
    setSelectedUser(user);
    setIsHistoryDialogOpen(true);
    fetchPlanHistory(user.user_id);
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'ativar' : 'desativar';
    
    if (!window.confirm(`Deseja ${action} o usuário ${user.name || user.email}?`)) {
      return;
    }
    
    try {
      await axios.put(
        `${API_URL}/admin/users/${user.user_id}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Usuário ${newStatus === 'active' ? 'ativado' : 'desativado'}!`);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const openProfileDialog = async (user) => {
    setProfileUser(user);
    setLoadingProfile(true);
    setEditingProfile(false);
    setIsProfileDialogOpen(true);
    try {
      const res = await axios.get(`${API_URL}/admin/profiles/${user.user_id}`, { withCredentials: true });
      setProfileData(res.data.profile || {});
    } catch (e) {
      setProfileData({});
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileUser || !profileData) return;
    setSavingProfile(true);
    try {
      await axios.put(
        `${API_URL}/admin/profiles/${profileUser.user_id}`,
        profileData,
        { withCredentials: true }
      );
      toast.success('Perfil atualizado!');
      setEditingProfile(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.join(', ') : detail || 'Erro ao salvar');
    } finally {
      setSavingProfile(false);
    }
  };

  const getPlanBadge = (subscription) => {
    if (!subscription) return null;
    const plan = PLANS.find(p => p.id === subscription.plan);
    if (!plan) return null;
    
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${plan.color}`}>
          {plan.name}
        </span>
        {subscription.admin_override && (
          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-medium flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Admin
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="manage-users-title">
              Gerenciar Usuários
            </h1>
            <p className="text-lg text-gray-600">
              Controle total sobre usuários e planos
            </p>
          </div>
          <Button
            onClick={fetchUsers}
            variant="outline"
            data-testid="refresh-users-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome, email ou ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-users-input"
              />
            </div>
            
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-lg"
              data-testid="role-filter"
            >
              <option value="all">Todos os tipos</option>
              <option value="seller">Vendedores</option>
              <option value="customer">Clientes</option>
              <option value="admin">Admins</option>
            </select>
            
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-lg"
              data-testid="plan-filter"
            >
              <option value="all">Todos os planos</option>
              {PLANS.map(plan => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
            
            <div className="flex items-center justify-end text-sm text-gray-600">
              <Users className="w-4 h-4 mr-2" />
              {filteredUsers.length} usuário(s)
            </div>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader className="w-12 h-12 animate-spin text-indigo-600" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">Nenhum usuário encontrado</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plano</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Créditos</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.user_id} className="hover:bg-gray-50" data-testid={`user-row-${user.user_id}`}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{user.name || 'Sem nome'}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          {user.store_id && (
                            <p className="text-xs text-indigo-600 font-mono">ID: {user.store_id}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.role === 'admin' ? 'bg-red-100 text-red-800' :
                          user.role === 'seller' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role === 'admin' ? 'Admin' : user.role === 'seller' ? 'Vendedor' : 'Cliente'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {getPlanBadge(user.subscription)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[user.status || 'active']}`}>
                          {user.status === 'active' ? 'Ativo' : user.status === 'suspended' ? 'Suspenso' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {user.subscription?.credits_remaining || 0} / {user.subscription?.credits_total || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openProfileDialog(user)}
                            title="Ver Perfil Completo"
                            data-testid={`view-profile-${user.user_id}`}
                          >
                            <FileText className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(user)}
                            title="Alterar plano"
                            data-testid={`edit-plan-${user.user_id}`}
                          >
                            <Crown className="w-4 h-4 text-amber-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openHistoryDialog(user)}
                            title="Histórico"
                            data-testid={`history-${user.user_id}`}
                          >
                            <History className="w-4 h-4 text-gray-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(user)}
                            title={user.status === 'active' ? 'Desativar' : 'Ativar'}
                            data-testid={`toggle-status-${user.user_id}`}
                          >
                            {user.status === 'active' ? (
                              <XCircle className="w-4 h-4 text-red-500" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Plan Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar Plano</DialogTitle>
            <DialogDescription>
              Modificar plano de {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Atenção!</p>
                  <p>Você está alterando manualmente o plano deste usuário. Isso pode impactar a cobrança.</p>
                </div>
              </div>
            </div>

            <div>
              <Label>Plano Atual</Label>
              <p className="text-lg font-medium text-gray-900 capitalize">
                {selectedUser?.subscription?.plan || 'Nenhum'}
              </p>
            </div>

            <div>
              <Label>Novo Plano</Label>
              <select
                value={newPlan}
                onChange={(e) => setNewPlan(e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg"
                data-testid="new-plan-select"
              >
                {PLANS.map(plan => (
                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="adminOverride"
                checked={adminOverride}
                onChange={(e) => setAdminOverride(e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded"
                data-testid="admin-override-checkbox"
              />
              <div>
                <label htmlFor="adminOverride" className="font-medium text-gray-900 cursor-pointer">
                  Override Admin
                </label>
                <p className="text-xs text-gray-500">
                  Ignora cobrança automática e limitações padrão
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleUpdatePlan}
                disabled={saving}
                className="flex-1"
                data-testid="confirm-plan-btn"
              >
                {saving ? (
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Confirmar Alteração
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Histórico de Alterações</DialogTitle>
            <DialogDescription>
              Alterações de plano de {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="pt-4">
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Loader className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : planHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                Nenhuma alteração registrada
              </p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {planHistory.map((entry, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <History className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 capitalize">{entry.old_plan}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-medium text-indigo-600 capitalize">{entry.new_plan}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(entry.changed_at).toLocaleString('pt-BR')}
                        {entry.changed_by && ` • Por: ${entry.changed_by}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Perfil Completo - {profileUser?.name || profileUser?.email}
            </DialogTitle>
            <DialogDescription>
              {profileUser?.role === 'seller' ? 'Dados do Lojista' : 'Dados do Cliente'}
            </DialogDescription>
          </DialogHeader>

          {loadingProfile ? (
            <div className="flex justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : !profileData ? (
            <p className="text-center py-8 text-gray-500">Nenhum dado adicional cadastrado</p>
          ) : (
            <div className="space-y-4 pt-2">
              {/* Toggle edit mode */}
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant={editingProfile ? "default" : "outline"}
                  onClick={() => setEditingProfile(!editingProfile)}
                  data-testid="toggle-edit-profile"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  {editingProfile ? 'Cancelar Edição' : 'Editar'}
                </Button>
              </div>

              {/* User basic info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-semibold text-gray-700">Dados Básicos</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Email:</span> <span className="font-medium">{profileUser?.email}</span></div>
                  <div><span className="text-gray-500">Tipo:</span> <span className="font-medium capitalize">{profileUser?.role === 'seller' ? 'Lojista' : 'Cliente'}</span></div>
                  {profileUser?.store_id && <div><span className="text-gray-500">Store ID:</span> <span className="font-mono text-indigo-600">{profileUser?.store_id}</span></div>}
                </div>
              </div>

              {/* Profile fields */}
              <div className="grid grid-cols-2 gap-4">
                {profileUser?.role === 'seller' ? (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">Nome Completo / Razão Social</Label>
                      {editingProfile ? (
                        <Input value={profileData.full_name || ''} onChange={e => setProfileData(p => ({ ...p, full_name: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{profileData.full_name || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Nome da Loja</Label>
                      {editingProfile ? (
                        <Input value={profileData.store_name || ''} onChange={e => setProfileData(p => ({ ...p, store_name: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{profileData.store_name || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Documento ({profileData.document_type?.toUpperCase() || 'CPF/CNPJ'})</Label>
                      {editingProfile ? (
                        <Input value={profileData.document_number || ''} onChange={e => setProfileData(p => ({ ...p, document_number: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1 font-mono">{profileData.document_number || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Telefone</Label>
                      {editingProfile ? (
                        <Input value={profileData.phone || ''} onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{profileData.phone || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Segmento</Label>
                      {editingProfile ? (
                        <Input value={profileData.business_segment || ''} onChange={e => setProfileData(p => ({ ...p, business_segment: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{profileData.business_segment || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Vitrine</Label>
                      <p className="text-sm font-medium mt-1">{profileData.storefront_name || '—'}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">Nome Completo</Label>
                      {editingProfile ? (
                        <Input value={profileData.full_name || ''} onChange={e => setProfileData(p => ({ ...p, full_name: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{profileData.full_name || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">CPF</Label>
                      {editingProfile ? (
                        <Input value={profileData.cpf || ''} onChange={e => setProfileData(p => ({ ...p, cpf: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1 font-mono">{profileData.cpf || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Telefone</Label>
                      {editingProfile ? (
                        <Input value={profileData.phone || ''} onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))} className="mt-1" />
                      ) : (
                        <p className="text-sm font-medium mt-1">{profileData.phone || '—'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Email</Label>
                      <p className="text-sm font-medium mt-1">{profileData.email || '—'}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Address */}
              {profileData.address && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                    <MapPin className="w-4 h-4" /> Endereço
                  </h3>
                  {editingProfile ? (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <Label className="text-xs">CEP</Label>
                        <Input value={profileData.address?.cep || ''} onChange={e => setProfileData(p => ({ ...p, address: { ...(p.address || {}), cep: e.target.value } }))} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Rua</Label>
                        <Input value={profileData.address?.street || ''} onChange={e => setProfileData(p => ({ ...p, address: { ...(p.address || {}), street: e.target.value } }))} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Número</Label>
                        <Input value={profileData.address?.number || ''} onChange={e => setProfileData(p => ({ ...p, address: { ...(p.address || {}), number: e.target.value } }))} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Bairro</Label>
                        <Input value={profileData.address?.neighborhood || ''} onChange={e => setProfileData(p => ({ ...p, address: { ...(p.address || {}), neighborhood: e.target.value } }))} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Cidade</Label>
                        <Input value={profileData.address?.city || ''} onChange={e => setProfileData(p => ({ ...p, address: { ...(p.address || {}), city: e.target.value } }))} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Estado</Label>
                        <Input value={profileData.address?.state || ''} onChange={e => setProfileData(p => ({ ...p, address: { ...(p.address || {}), state: e.target.value } }))} className="mt-1" />
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700">
                      {profileData.address?.street && <p>{profileData.address.street}, {profileData.address.number} {profileData.address.complement && `- ${profileData.address.complement}`}</p>}
                      {profileData.address?.neighborhood && <p>{profileData.address.neighborhood}</p>}
                      {profileData.address?.city && <p>{profileData.address.city} - {profileData.address.state}</p>}
                      {profileData.address?.cep && <p className="text-gray-500">CEP: {profileData.address.cep}</p>}
                      {!profileData.address?.street && <p className="text-gray-400">Endereço não cadastrado</p>}
                    </div>
                  )}
                </div>
              )}

              {/* Save button when editing */}
              {editingProfile && (
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? <Loader className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Salvar Alterações
                  </Button>
                </div>
              )}

              {/* Profile status */}
              <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t">
                {profileData.profile_complete ? (
                  <><CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> Cadastro completo</>
                ) : (
                  <><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Cadastro incompleto</>
                )}
                {profileData.admin_edited && <span className="ml-2 text-blue-500">Editado pelo admin</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
